import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getUsersConstans } from '~/common/constants/users';
import { AvatarPanel } from '~/components/SettingsPage/AccountInformation/AvatarPanel/AvatarPanel';
import { Button } from '~/eds/src/components/Button/Button';
import { Card } from '~/eds/src/components/Card/Card';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';

export const AccountInformation = ({
  isWorkdayUser,
}: {
  isWorkdayUser?: boolean;
}) => {
  const intl = useIntl();
  const currentUser = useCurrentUser();
  const { toast } = useToast();
  const { WORKDAY_ADMIN_DISABLED_ACTION_MESSAGE } = getUsersConstans(intl);

  const [updateCurrentUser] = api.endpoints.updateCurrentUser.useMutation();

  const [user, setUser] = useState({
    firstName: currentUser?.first_name,
    lastName: currentUser?.last_name,
    email: currentUser?.email,
    avatar: currentUser?.avatar,
    client: currentUser?.client,
  });

  const isAvatarViable = 'avatar' in user && user.avatar !== undefined;

  return (
    <Card
      mode="bordered"
      header={{
        title: intl.formatMessage({
          id: 'AccountInformation.cardTitle',
          defaultMessage: 'Account Information',
          description: 'Title for the account information card',
        }),
      }}
    >
      <Layout direction="row" justify="space-between">
        <Layout preset="form-fields">
          <Layout direction="row" spacing={6}>
            <FormField<string, false>
              required
              input={TextInput}
              label={intl.formatMessage({
                id: 'AccountInformation.firstNameLabel',
                defaultMessage: 'First Name',
                description: 'Label for first name input field',
              })}
              name="first_name"
              value={user.firstName}
              onChange={(updatedFirstName) => {
                setUser({ ...user, firstName: updatedFirstName ?? '' });
              }}
              disabled={isWorkdayUser}
            />
            <FormField<string, false>
              required
              input={TextInput}
              label={intl.formatMessage({
                id: 'AccountInformation.lastNameLabel',
                defaultMessage: 'Last Name',
                description: 'Label for last name input field',
              })}
              name="last_name"
              value={user.lastName}
              onChange={(updatedLastName) => {
                setUser({ ...user, lastName: updatedLastName ?? '' });
              }}
              disabled={isWorkdayUser}
            />
          </Layout>
          <FormField
            info={intl.formatMessage({
              id: 'AccountInformation.securityMessage',
              defaultMessage:
                'Due to security reasons, we do not allow changing your email address. If you wish to update this, please contact your organization administrator.',
              description:
                'Security message explaining email cannot be changed',
            })}
            input={TextInput}
            label={intl.formatMessage({
              id: 'AccountInformation.emailLabel',
              defaultMessage: 'Email',
              description: 'Label for email input field',
            })}
            name="email"
            value={user.email}
            onChange={() => {}}
            disabled={true}
          />
          <Button
            id="user_accountInformation_saveChangesButton"
            variant="primary"
            text={intl.formatMessage({
              id: 'AccountInformation.saveChangesButton',
              defaultMessage: 'Save Changes',
              description: 'Save changes button text',
            })}
            tooltip={WORKDAY_ADMIN_DISABLED_ACTION_MESSAGE}
            onClick={() => {
              updateCurrentUser({
                user,
              })
                .then(() => {
                  toast({
                    status: 'success',
                    message: intl.formatMessage({
                      id: 'AccountInformation.updateSuccessMessage',
                      defaultMessage: 'User information has been updated.',
                      description:
                        'Success message when user information is updated',
                    }),
                  });
                })
                .catch(() => {
                  toast({
                    status: 'danger',
                    message: intl.formatMessage({
                      id: 'AccountInformation.updateErrorMessage',
                      defaultMessage: 'First and last name cannot be empty.',
                      description:
                        'Error message when required fields are empty',
                    }),
                  });
                });
            }}
            disabled={
              isWorkdayUser ||
              !user.firstName?.trim().length ||
              !user.lastName?.trim().length
            }
          />
        </Layout>
        {isAvatarViable && (
          <Layout direction="column">
            <Text variant="tiny">
              <FormattedMessage
                id="AccountInformation.profilePictureLabel"
                defaultMessage="PROFILE PICTURE"
                description="Label for profile picture section"
              />
            </Text>
            <AvatarPanel user={user} setCurrentUser={setUser} />
          </Layout>
        )}
      </Layout>
    </Card>
  );
};
